import { useEffect, useRef, useState } from 'react'
import Check from 'lucide-react/dist/esm/icons/check.mjs'
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down.mjs'
import CloudUpload from 'lucide-react/dist/esm/icons/cloud-upload.mjs'
import Download from 'lucide-react/dist/esm/icons/download.mjs'
import Package from 'lucide-react/dist/esm/icons/package.mjs'
import Plus from 'lucide-react/dist/esm/icons/plus.mjs'
import { useNavigate } from 'react-router-dom'
import { useProjectData } from '../store/AppStore'
import { ContentTemplateIdentity } from '../shared/ui/ContentTemplateIdentity'
import { ModalShell } from '../shared/ui/ModalShell'
import { PlatformIcon } from '../shared/ui/PlatformIcon'
import { createId } from '../shared/utils/id'
import { getShanghaiDate } from '../shared/utils/date'
import { contentTypeLabels, platformMeta } from '../shared/utils/formatters'
import { getPublicationId, isActivePlanStatus, publishablePlatforms } from '../shared/utils/publications'
import { downloadImageWorkPackageTemplate, downloadLiveWorkPackageTemplate, downloadVideoWorkPackageTemplate } from '../shared/utils/workPackageTemplate'
import { ReferenceWorkbench } from '../features/references/ReferenceWorkbench'
import { allocateRuleTaskAssets, buildRuleTasks, distributeTimes, getTimeDistributionMetrics, getTimePosition } from '../features/schedule/scheduleUtils'

const getTemplatesForAccount = (account, templates, skuMap) => templates.filter(template => (account?.productIds || []).includes(skuMap.get(template.skuId)?.productId) && (template.platforms || []).includes(account?.platform))
const formatTimeGap = value => value === null ? '—' : value >= 60 ? `${Math.floor(value / 60)} 小时${value % 60 ? ` ${value % 60} 分` : ''}` : `${value} 分钟`

function UploadWorkDialog({ payload, close }) {
  const { project, filter, upsert, remove, uploadAssetFiles, batchImportAssets, notify } = useProjectData()
  const templates = filter('templates')
  const products = filter('products')
  const skus = filter('skus')
  const productMap = new Map(products.map(item => [item.id, item]))
  const skuMap = new Map(skus.map(item => [item.id, item]))
  const initialTemplateId = payload.templateId || templates.find(item => item.skuId === payload.skuId)?.id || ''
  const [files, setFiles] = useState([])
  const [templateId, setTemplateId] = useState(initialTemplateId)
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false)
  const [uploadMode, setUploadMode] = useState('single')
  const [copy, setCopy] = useState({ title: '', body: '', tags: '' })
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [batchResult, setBatchResult] = useState(null)
  const templatePickerRef = useRef(null)
  const selectedTemplate = templates.find(item => item.id === templateId)
  const selectedSku = skuMap.get(selectedTemplate?.skuId)
  const selectedProduct = productMap.get(selectedSku?.productId)
  const skuId = selectedSku?.id || ''
  const contentType = selectedTemplate?.type || ''
  const isImage = contentType === 'image'
  const isVideo = contentType === 'video'
  const isLive = contentType === 'live'
  const isBatch = uploadMode === 'batch'
  const accept = isBatch ? '.zip,application/zip' : isVideo ? 'video/*' : isLive ? 'image/*,video/*' : 'image/*'
  const allowMultiple = !isBatch && (isImage || isLive)
  const workCount = !files.length || isBatch ? 0 : isVideo ? files.length : 1
  const uploadCopy = !isBatch
  const showPackageTemplate = isBatch
  const packageTree = isVideo ? `批量视频作品.zip
├── 001-作品A/
│   ├── 作品文案.txt
│   └── video/
│       └── 作品视频.mp4
└── 002-作品B/
    ├── 作品文案.txt
    └── video/
        └── 作品视频.mp4` : isLive ? `批量Live图作品.zip
├── 001-作品A/
│   ├── 作品文案.txt
│   └── media/
│       ├── 01.jpg
│       └── 02.mp4
└── 002-作品B/
    ├── 作品文案.txt
    └── media/` : `批量图文作品.zip
├── 001-作品A/
│   ├── 作品文案.txt
│   └── images/
│       ├── 01.jpg
│       └── 02.jpg
└── 002-作品B/
    ├── 作品文案.txt
    └── images/`
  const packageCopyExample = isVideo ? `标题：
在这里填写作品标题

正文：
在这里填写作品正文，可以写多行

标签：（每行填写一个标签，不要加 #）
咖啡
通勤
办公室

视频文件名：
作品视频.mp4` : isLive ? `标题：
在这里填写作品标题

正文：
在这里填写作品正文，可以写多行

标签：（每行填写一个标签，不要加 #）
咖啡
通勤
办公室

素材顺序：（每行填写一个图片或视频文件名）
01.jpg
02.mp4
03.jpg` : `标题：
在这里填写作品标题

正文：
在这里填写作品正文，可以写多行

标签：（每行填写一个标签，不要加 #）
咖啡
通勤
办公室

图片顺序：（每行填写一个图片文件名）
01.jpg
02.jpg
03.jpg`

  useEffect(() => {
    if (!templatePickerOpen) return undefined
    const closePicker = event => {
      if (!templatePickerRef.current?.contains(event.target)) setTemplatePickerOpen(false)
    }
    document.addEventListener('mousedown', closePicker)
    return () => document.removeEventListener('mousedown', closePicker)
  }, [templatePickerOpen])

  const changeTemplate = nextTemplateId => {
    setTemplateId(nextTemplateId)
    setTemplatePickerOpen(false)
    setUploadMode('single')
    setFiles([])
    setBatchResult(null)
    setCopy({ title: '', body: '', tags: '' })
  }
  const changeMode = mode => {
    setUploadMode(mode)
    setFiles([])
    setBatchResult(null)
  }
  const selectFiles = event => {
    const selectedFiles = Array.from(event.target.files || [])
    setFiles(allowMultiple ? selectedFiles : selectedFiles.slice(0, 1))
    setBatchResult(null)
  }
  const getPrompt = () => {
    if (isBatch) return ['上传一个批量作品 ZIP', '每个一级作品文件夹会解析为一条独立作品']
    if (isImage) return ['上传这条作品的全部图片', '可一次选择多张图片，按选择顺序保存']
    if (isVideo) return ['上传一个视频作品', '支持 MP4、MOV、WebM']
    return ['上传这条 Live 图的全部素材', '可选择图片和视频素材']
  }

  const save = async () => {
    setSaving(true)
    try {
      if (isBatch) {
        const result = await batchImportAssets(project.id, { archive: files[0], templateId, note })
        setBatchResult(result)
        setFiles([])
        if (result.failed) {
          notify(`批量导入完成：成功 ${result.succeeded} 条，失败 ${result.failed} 条`, 'error')
        } else {
          notify(`已成功导入 ${result.succeeded} 条作品`)
          close()
        }
        return
      }
      const entries = [{ files, title: copy.title.trim() || `${selectedTemplate.name} · ${contentTypeLabels[contentType]}作品` }]
      for (const [index, entry] of entries.entries()) {
        const asset = {
        id: createId('asset'), code: `WK-${Date.now()}-${index + 1}`, projectId: project.id,
        title: entry.title, templateId, skuId, accountId: '', type: contentType,
        duration: isVideo ? '--:--' : isImage ? `${entry.files.length} 张` : `${entry.files.length} 个素材`,
        status: 'unpublished', uploadedAt: new Date().toISOString(), thumbnailTone: isVideo ? 'office' : isLive ? 'city' : 'paper', note,
        uploadMode, fileNames: entry.files.map(file => file.name), copyTitle: uploadCopy ? copy.title.trim() : '', copyBody: uploadCopy ? copy.body.trim() : '', copyTags: uploadCopy ? copy.tags.split(/[，,\n]/).map(item => item.trim().replace(/^#/, '')).filter(Boolean) : [],
        }
        await upsert('assets', asset)
        try {
          const uploadResult = await uploadAssetFiles(project.id, asset.id, entry.files)
          if (uploadResult.asset) await upsert('assets', uploadResult.asset)
        } catch (error) {
          await remove('assets', asset.id)
          throw error
        }
      }
      notify(`已添加 ${entries.length} 条作品到库存`)
      close()
    } catch (error) {
      notify(error instanceof Error ? error.message : '作品上传未完成，请检查文件后重试', 'error')
    } finally {
      setSaving(false)
    }
  }

  const [promptTitle, promptText] = selectedTemplate ? getPrompt() : ['', '']
  const copyComplete = !uploadCopy || Boolean(copy.title.trim() && copy.body.trim() && copy.tags.split(/[，,\n]/).some(item => item.trim()))

  return <ModalShell eyebrow="CONTENT UPLOAD" title="上传作品" close={close} className="upload-work-modal" footer={<><button className="btn secondary" disabled={saving} onClick={close}>取消</button><button className="btn primary" disabled={saving || !files.length || !templateId || !skuId || !copyComplete} onClick={save}>{saving ? '正在解析并上传…' : isBatch ? '解析并批量导入' : workCount ? `保存 ${workCount} 条作品` : '保存到库存'}</button></>}>
    <div className="modal-content upload-work-content">
      <section className="upload-template-step"><div className="upload-step-label"><i>1</i><b>选择关联模板</b></div><div className="upload-template-picker" ref={templatePickerRef}><button type="button" className={`upload-template-trigger${templatePickerOpen ? ' open' : ''}`} aria-haspopup="listbox" aria-expanded={templatePickerOpen} onClick={() => setTemplatePickerOpen(current => !current)}>{selectedTemplate ? <ContentTemplateIdentity template={selectedTemplate} productName={selectedProduct?.name || '产品已移除'} skuName={selectedSku?.name || 'SKU 已移除'} variant="compact"/> : <span className="upload-template-placeholder"><Package size={18}/>请选择内容模板</span>}<ChevronDown className="upload-template-chevron" size={17}/></button>{templatePickerOpen ? <div className="upload-template-options" role="listbox">{templates.map(item => { const sku = skuMap.get(item.skuId); const product = productMap.get(sku?.productId); const selected = item.id === templateId; return <button type="button" className={`upload-template-option${selected ? ' selected' : ''}`} role="option" aria-selected={selected} key={item.id} onClick={() => changeTemplate(item.id)}><ContentTemplateIdentity template={item} productName={product?.name || '产品已移除'} skuName={sku?.name || 'SKU 已移除'} variant="compact"/>{selected ? <Check size={16}/> : null}</button> })}{!templates.length ? <div className="upload-template-options-empty">当前还没有内容模板</div> : null}</div> : null}</div></section>
      {!selectedTemplate ? <div className="upload-template-empty"><Package size={24}/><b>{templates.length ? '选择模板后继续上传' : '当前还没有内容模板'}</b><span>{templates.length ? '上传界面会根据模板类型自动切换。' : '请先创建模板，再上传对应作品。'}</span></div> : <>
        <div className="upload-platform-summary"><b>适用平台</b><div>{selectedTemplate.platforms.map(platform => <span key={platform}><PlatformIcon platform={platform} small/>{platformMeta[platform]?.label}</span>)}</div><small>作品只上传一次，各平台分别计算发布状态</small></div>
        <div className="upload-mode-tabs"><button type="button" className={uploadMode === 'single' ? 'active' : ''} onClick={() => changeMode('single')}>单个作品上传</button><button type="button" className={uploadMode === 'batch' ? 'active' : ''} onClick={() => changeMode('batch')}>批量上传</button></div>
        <section className="upload-material-section"><div className="upload-section-head"><i>2</i><b>{contentTypeLabels[contentType]}素材</b></div><label className="upload-dropzone"><input key={`${templateId}-${uploadMode}`} className="visually-hidden" type="file" multiple={allowMultiple} accept={accept} onChange={selectFiles}/><div><CloudUpload size={25}/></div><h3>{files.length ? isBatch ? '已选择批量 ZIP' : `已选择 ${files.length} 个文件` : promptTitle}</h3><p>{files.length ? files.map(file => file.name).join('、') : promptText}</p><span className="btn secondary">选择本地文件</span></label></section>
        {showPackageTemplate ? <section className="upload-package-spec"><div className="upload-package-spec-head"><div><b>批量 ZIP 规范</b><span>一个 ZIP，多条作品</span></div><div><small>每个作品文件夹会独立解析并写入 OSS</small><button type="button" onClick={() => { if (isVideo) downloadVideoWorkPackageTemplate(); else if (isLive) downloadLiveWorkPackageTemplate(); else downloadImageWorkPackageTemplate(); notify('批量 ZIP 模板已下载') }}><Download size={12}/>下载批量模板</button></div></div><div className="upload-package-spec-grid"><pre>{packageTree}</pre><div><code>{packageCopyExample}</code></div></div><ul><li><b>文件夹</b><span>每个作品使用一个独立文件夹</span></li><li><b>文案</b><span>每个文件夹必须包含“作品文案.txt”</span></li><li><b>标签</b><span>每条作品至少填写一个标签</span></li><li><b>{isVideo ? '视频文件名' : isLive ? '素材顺序' : '图片顺序'}</b><span>{isVideo ? '填写 video 文件夹中的视频名称' : '每行一个文件名，顺序就是发布顺序'}</span></li></ul></section> : null}
        {batchResult?.failed ? <section className="upload-batch-result"><div><b>批量导入结果</b><span>成功 {batchResult.succeeded} 条，失败 {batchResult.failed} 条</span></div><ul>{batchResult.results.filter(item => item.status === 'failed').map(item => <li key={item.folderName}><b>{item.folderName}</b><span>{item.message}</span></li>)}</ul><small>已成功的作品已入库。请修改失败文件夹后，只将失败项重新打包上传。</small></section> : null}
        {uploadCopy ? <section className="upload-copy-section"><div className="upload-section-head"><i>3</i><b>作品文案</b><small>标题、正文和标签均为必填</small></div><div className="upload-copy-grid"><label><span>标题 *</span><input value={copy.title} onChange={event => setCopy(current => ({ ...current, title: event.target.value }))}/></label><label><span>标签 *</span><input placeholder="多个标签用逗号分隔" value={copy.tags} onChange={event => setCopy(current => ({ ...current, tags: event.target.value }))}/></label><label className="full"><span>正文 *</span><textarea value={copy.body} onChange={event => setCopy(current => ({ ...current, body: event.target.value }))}/></label></div></section> : null}
        <label className="form-label upload-note"><span>作品备注</span><textarea value={note} onChange={event => setNote(event.target.value)}/></label>
      </>}
    </div>
  </ModalShell>
}

function TemplateDialog({ payload, close }) {
  const navigate = useNavigate()
  const { project, filter, upsert, update, notify } = useProjectData()
  const templates = filter('templates')
  const products = filter('products')
  const skus = filter('skus')
  const references = filter('references')
  const assets = filter('assets')
  const plans = filter('plans')
  const accounts = filter('accounts')
  const assetPublications = filter('assetPublications')
  const existing = templates.find(item => item.id === payload.templateId)
  const sourceReference = references.find(item => item.id === payload.referenceId)
  const [step, setStep] = useState(1)
  const [activeReferenceId, setActiveReferenceId] = useState(sourceReference?.id || existing?.referenceIds?.[0] || references[0]?.id || '')
  const [form, setForm] = useState(() => existing ? { ...existing } : {
    name: '', type: sourceReference?.type || 'video', platforms: sourceReference?.platform ? [sourceReference.platform] : [], referenceIds: sourceReference ? [sourceReference.id] : [],
    skuId: payload.skuId || '', description: '', structure: '', tone: 'purple',
    sceneDescription: '', propsDescription: '', modelDescription: '', shotAnalysis: '', shootingInstructions: '', hookAnalysis: '', batchShootingPlan: '',
  })
  const set = (key, value) => setForm(current => ({ ...current, [key]: value }))
  const toggleReference = referenceId => set('referenceIds', form.referenceIds.includes(referenceId) ? form.referenceIds.filter(item => item !== referenceId) : [...form.referenceIds, referenceId])
  const accountMap = new Map(accounts.map(account => [account.id, account]))
  const templateAssetIds = new Set(assets.filter(asset => asset.templateId === existing?.id).map(asset => asset.id))
  const lockedPlatforms = new Set([
    ...plans.filter(plan => plan.templateId === existing?.id).map(plan => plan.platform || accountMap.get(plan.accountId)?.platform),
    ...assetPublications.filter(publication => templateAssetIds.has(publication.assetId)).map(publication => publication.platform),
  ].filter(Boolean))
  const togglePlatform = platform => {
    const selected = form.platforms.includes(platform)
    if (selected && lockedPlatforms.has(platform)) {
      notify('该平台已有排期或发布记录，不能移除', 'error')
      return
    }
    set('platforms', selected ? form.platforms.filter(item => item !== platform) : [...form.platforms, platform])
  }
  const typeOptions = [['image', '图文'], ['video', '视频'], ['live', 'Live 图']]
  const selectedSku = skus.find(item => item.id === form.skuId)
  const skuLocked = Boolean(existing && (assets.some(item => item.templateId === existing.id) || plans.some(item => item.templateId === existing.id)))
  const canContinue = step === 1 ? form.referenceIds.length > 0 : step === 2 ? Boolean(form.skuId) : Boolean(form.name.trim() && form.type && form.platforms.length)

  const save = () => {
    const entity = {
      ...form, skuId: skuLocked ? existing.skuId : form.skuId, id: existing?.id || createId('template'), code: existing?.code || `TP-${String(100 + templates.length + 1)}`,
      platforms: form.platforms,
      tone: existing?.tone || { image: 'blue', video: 'orange', live: 'purple' }[form.type],
      projectId: project.id, stock: existing?.stock || 0, published: existing?.published || 0,
      interactions: existing?.interactions || 0, rate: existing?.rate || 0,
    }
    upsert('templates', entity)
    references.forEach(reference => {
      const selected = form.referenceIds.includes(reference.id)
      const linked = reference.templateIds.includes(entity.id)
      if (selected && !linked) update('references', reference.id, { templateIds: [...reference.templateIds, entity.id] })
      if (!selected && linked) update('references', reference.id, { templateIds: reference.templateIds.filter(id => id !== entity.id) })
    })
    notify(existing ? '模板基础信息已更新' : '模板已创建')
    close()
    navigate(`/templates/${entity.id}`)
  }

  return <ModalShell eyebrow="TEMPLATE BUILDER" title={existing ? '编辑模板基础信息' : '创建内容模板'} close={close} className="template-builder-modal" footer={<><button className="btn secondary" onClick={close}>取消</button>{step > 1 ? <button className="btn secondary" onClick={() => setStep(current => current - 1)}>上一步</button> : null}<button className="btn primary" disabled={!canContinue} onClick={() => step < 3 ? setStep(current => current + 1) : save()}>{step < 3 ? '下一步' : existing ? '保存修改' : '创建模板'}</button></>}>
    <div className="modal-content"><div className="steps template-wizard-steps"><span className={step >= 1 ? 'active' : ''}><i>1</i>选择参考作品</span><b/><span className={step >= 2 ? 'active' : ''}><i>2</i>选择产品 SKU</span><b/><span className={step >= 3 ? 'active' : ''}><i>3</i>模板信息</span></div>
      {step === 1 ? <div className="template-wizard-section"><div className="wizard-section-head"><div><b>选择参考作品</b><span>浏览全部作品，播放视频或滑动图片，确认完整信息后可多选。</span></div><small>已选择 {form.referenceIds.length} 个</small></div><ReferenceWorkbench references={references} selectedIds={form.referenceIds} activeId={activeReferenceId} onActiveChange={setActiveReferenceId} onToggle={toggleReference} filterable selectable/></div> : null}
      {step === 2 ? <div className="template-wizard-section"><div className="wizard-section-head"><div><b>选择产品 SKU</b><span>{skuLocked ? '已有作品或排期，当前 SKU 不可修改。' : '每个内容模板只能绑定一个产品 SKU。'}</span></div><small>{selectedSku ? '已选择' : '请选择'}</small></div><div className="template-product-picker">{products.map(product => { const productSkus = skus.filter(sku => sku.productId === product.id); return productSkus.length ? <section key={product.id}><div><Package size={14}/><b>{product.name}</b></div><div>{productSkus.map(sku => <button type="button" className={form.skuId === sku.id ? 'selected' : ''} disabled={skuLocked} key={sku.id} onClick={() => set('skuId', sku.id)}><span>{form.skuId === sku.id ? <Check size={12}/> : <Plus size={12}/>}</span><div><b>{sku.name}</b></div></button>)}</div></section> : null })}</div></div> : null}
      {step === 3 ? <div className="template-wizard-section template-info-step"><div className="wizard-section-head"><div><b>填写模板信息</b><span>命名并选择模板类型和适用平台。</span></div></div><label><span>模板名称</span><input value={form.name} onChange={event => set('name', event.target.value)} placeholder="例如：办公室 3 秒反差开场"/></label><label><span>模板类型</span><div className="template-type-picker">{typeOptions.map(([value, label]) => <button type="button" className={form.type === value ? 'selected' : ''} key={value} onClick={() => set('type', value)}>{form.type === value ? <Check size={13}/> : null}<b>{label}</b></button>)}</div></label><label><span>适用平台</span><div className="template-platform-picker">{publishablePlatforms.map(platform => { const selected = form.platforms.includes(platform); return <button type="button" className={selected ? 'selected' : ''} key={platform} onClick={() => togglePlatform(platform)}><PlatformIcon platform={platform} small/><b>{platformMeta[platform]?.label}</b>{selected ? <Check size={12}/> : null}</button> })}</div></label><div className="wizard-final-summary"><span>{form.referenceIds.length} 个参考作品</span><i/><span>{selectedSku?.name || '未选择 SKU'}</span><i/><span>{contentTypeLabels[form.type]}</span><i/><span>{form.platforms.length} 个平台</span></div></div> : null}
    </div>
  </ModalShell>
}

function PublishSettingsDialog({ payload, close }) {
  const { project, filter, upsert, remove, notify } = useProjectData()
  const accounts = filter('accounts')
  const products = filter('products')
  const templates = filter('templates')
  const skus = filter('skus')
  const assets = filter('assets')
  const assetPublications = filter('assetPublications')
  const plans = filter('plans')
  const settings = filter('publishSettings')
  const [activeAccountId, setActiveAccountId] = useState(payload.accountId || accounts[0]?.id || '')
  const [drafts, setDrafts] = useState(() => {
    const settingMap = new Map(settings.map(setting => [setting.accountId, setting]))
    return Object.fromEntries(accounts.map(account => {
      const setting = settingMap.get(account.id)
      return [account.id, {
        startTime: setting?.startTime || '07:00',
        endTime: setting?.endTime || '23:00',
        distributionMode: setting?.distributionMode === 'uniform' ? 'uniform' : 'smart',
        counts: Object.fromEntries((setting?.rules || []).map(rule => [rule.templateId, Number(rule.dailyCount) || 0])),
      }]
    }))
  })
  const skuMap = new Map(skus.map(item => [item.id, item]))
  const productMap = new Map(products.map(item => [item.id, item]))
  const templateMap = new Map(templates.map(item => [item.id, item]))
  const activeAccount = accounts.find(item => item.id === activeAccountId)
  const activeTemplates = getTemplatesForAccount(activeAccount, templates, skuMap)
  const activeProducts = (activeAccount?.productIds || []).map(productId => productMap.get(productId)).filter(Boolean)
  const activeDraft = drafts[activeAccountId] || { startTime: '07:00', endTime: '23:00', distributionMode: 'smart', counts: {} }
  const activeRules = activeTemplates.map(template => ({ templateId: template.id, dailyCount: activeDraft.counts[template.id] || 0 }))
  const activeTasks = buildRuleTasks(activeRules)
  const activeTotal = activeTasks.length
  const activeTimes = distributeTimes(activeDraft.startTime, activeDraft.endTime, activeTotal, { mode: activeDraft.distributionMode, platform: activeAccount?.platform })
  const activeTimeMetrics = getTimeDistributionMetrics(activeTimes)
  const invalidWindow = activeDraft.startTime >= activeDraft.endTime

  const updateDraft = patch => setDrafts(current => ({ ...current, [activeAccountId]: { ...current[activeAccountId], ...patch } }))
  const updateCount = (templateId, amount) => updateDraft({ counts: { ...activeDraft.counts, [templateId]: Math.max(0, Math.min(20, (activeDraft.counts[templateId] || 0) + amount)) } })
  const getAccountTotal = accountId => {
    const account = accounts.find(item => item.id === accountId)
    return buildRuleTasks(getTemplatesForAccount(account, templates, skuMap).map(template => ({ templateId: template.id, dailyCount: drafts[accountId]?.counts?.[template.id] || 0 }))).length
  }

  const save = generatePlans => {
    const invalidAccount = accounts.find(account => getAccountTotal(account.id) > 0 && drafts[account.id].startTime >= drafts[account.id].endTime)
    if (invalidAccount) {
      setActiveAccountId(invalidAccount.id)
      notify('结束时间必须晚于开始时间', 'error')
      return
    }
    accounts.forEach(account => {
      const draft = drafts[account.id]
      const accountTemplates = getTemplatesForAccount(account, templates, skuMap)
      upsert('publishSettings', {
        id: settings.find(item => item.accountId === account.id)?.id || `publish-setting-${account.id}`,
        projectId: project.id,
        accountId: account.id,
        startTime: draft.startTime,
        endTime: draft.endTime,
        distributionMode: draft.distributionMode === 'uniform' ? 'uniform' : 'smart',
        rules: accountTemplates.map(template => ({ templateId: template.id, dailyCount: draft.counts[template.id] || 0 })).filter(rule => rule.dailyCount > 0),
      })
    })
    if (generatePlans) {
      const targetDate = payload.date || getShanghaiDate()
      let generatedCount = 0
      let skippedCount = 0
      const replaceablePlans = plans.filter(plan => plan.date === targetDate && (isActivePlanStatus(plan.status) || plan.status === 'failed'))
      const replaceablePlanIds = new Set(replaceablePlans.map(plan => plan.id))
      assetPublications.filter(publication => replaceablePlanIds.has(publication.planId)).forEach(publication => remove('assetPublications', publication.id))
      replaceablePlans.forEach(plan => remove('plans', plan.id))
      const unavailablePublicationKeys = new Set(assetPublications.filter(publication => publication.status === 'published' || (publication.status === 'scheduled' && !replaceablePlanIds.has(publication.planId))).map(publication => `${publication.assetId}:${publication.platform}`))
      accounts.forEach(account => {
        const draft = drafts[account.id]
        const accountTemplates = getTemplatesForAccount(account, templates, skuMap)
        const publishedCounts = new Map()
        plans.filter(plan => plan.date === targetDate && plan.accountId === account.id && plan.status === 'published').forEach(plan => publishedCounts.set(plan.templateId, (publishedCounts.get(plan.templateId) || 0) + 1))
        const remainingRules = accountTemplates.map(template => ({
          templateId: template.id,
          dailyCount: Math.max(0, (draft.counts[template.id] || 0) - (publishedCounts.get(template.id) || 0)),
        }))
        const tasks = buildRuleTasks(remainingRules)
        const allocationResult = allocateRuleTaskAssets(tasks, {
          templates: accountTemplates,
          assets,
          platform: account.platform,
          unavailablePublicationKeys,
        })
        skippedCount += allocationResult.skippedCount
        const times = distributeTimes(draft.startTime, draft.endTime, allocationResult.allocations.length, { mode: draft.distributionMode, platform: account.platform })
        allocationResult.allocations.forEach(({ template, asset }, index) => {
          const planId = createId('plan')
          upsert('plans', {
            id: planId, projectId: project.id, date: targetDate, time: times[index], accountId: account.id, platform: account.platform, templateId: template.id, skuId: template.skuId,
            assetId: asset.id, title: asset.title, status: 'pending', reminderEnabled: true,
          })
          upsert('assetPublications', {
            id: getPublicationId(asset.id, account.platform), projectId: project.id, assetId: asset.id, platform: account.platform,
            accountId: account.id, planId, status: 'scheduled', scheduledAt: `${targetDate}T${times[index]}:00`, publishedAt: '',
          })
          generatedCount += 1
        })
      })
      if (!generatedCount && skippedCount) notify(`发布设置已保存，但没有可用库存作品；${skippedCount} 条排期未生成`, 'error')
      else if (skippedCount) notify(`已生成 ${generatedCount} 条排期；库存不足，${skippedCount} 条未排期`, 'warning')
      else notify(`已保存发布设置并生成 ${generatedCount} 条 ${targetDate} 排期`)
    } else notify('发布设置已保存')
    close()
  }

  return <ModalShell eyebrow="PUBLISH SETTINGS" title="发布设置" close={close} className="publish-settings-modal" footer={<><button className="btn secondary" onClick={close}>取消</button><button className="btn secondary" onClick={() => save(false)}>保存设置</button><button className="btn primary" onClick={() => save(true)}>保存并生成当日排期</button></>}>
    <div className="publish-settings-layout">
      <aside className="publish-account-list"><div className="publish-setting-side-head"><b>发布账号</b><span>{accounts.length} 个</span></div>{accounts.map(account => { const total = getAccountTotal(account.id); const draft = drafts[account.id]; return <button type="button" className={activeAccountId === account.id ? 'active' : ''} onClick={() => setActiveAccountId(account.id)} key={account.id}><PlatformIcon platform={account.platform} small/><div><b>{account.name}</b><small>{total ? `每日 ${total} 条 · ${draft.startTime}-${draft.endTime}` : '未设置发布规则'}</small></div><span>{total}</span></button> })}</aside>
      <section className="publish-setting-editor">
        <div className="publish-setting-account-head"><div><PlatformIcon platform={activeAccount?.platform} small/><div><b>{activeAccount?.name}</b><span>{platformMeta[activeAccount?.platform]?.label}{activeProducts.length ? ` · ${activeProducts.map(product => product.name).join('、')}` : ' · 未关联产品'}</span></div></div><strong>每日 {activeTotal} 条</strong></div>
        <div className={`publish-time-window${invalidWindow ? ' invalid' : ''}`}><label><span>开始发布时间</span><input type="time" value={activeDraft.startTime} onChange={event => updateDraft({ startTime: event.target.value })}/></label><i>至</i><label><span>结束发布时间</span><input type="time" value={activeDraft.endTime} onChange={event => updateDraft({ endTime: event.target.value })}/></label></div>
        <div className="publish-strategy-row"><div><b>发布时间策略</b><span>{activeDraft.distributionMode === 'smart' ? `按${platformMeta[activeAccount?.platform]?.label || '平台'}活跃时段智能错峰` : '在发布区间内按时间槽均匀分布'}</span></div><div className="publish-strategy-options"><button type="button" className={activeDraft.distributionMode === 'smart' ? 'active' : ''} onClick={() => updateDraft({ distributionMode: 'smart' })}>智能分布</button><button type="button" className={activeDraft.distributionMode === 'uniform' ? 'active' : ''} onClick={() => updateDraft({ distributionMode: 'uniform' })}>均匀分布</button></div></div>
        <div className="publish-rule-head"><b>模板发布规则</b><span>设置该账号每个模板每天发布多少条</span></div>
        <div className="publish-rule-list">{activeTemplates.map(template => { const sku = skuMap.get(template.skuId); const product = productMap.get(sku?.productId); const count = activeDraft.counts[template.id] || 0; return <div className={count ? 'active' : ''} key={template.id}><ContentTemplateIdentity template={template} productName={product?.name || '产品已移除'} skuName={sku?.name || 'SKU 已移除'} variant="compact"/><div className="publish-rule-counter"><button type="button" disabled={!count} onClick={() => updateCount(template.id, -1)}>−</button><b>{count}</b><button type="button" onClick={() => updateCount(template.id, 1)}>＋</button><span>条 / 天</span></div></div> })}{!activeTemplates.length ? <div className="publish-rule-empty"><Package size={20}/><b>该账号还没有可用模板</b><span>请先在“设备与账号”中为账号关联产品。</span></div> : null}</div>
        <div className="publish-time-preview"><div className="publish-preview-head"><div><b>发布时间预览</b><span>{activeTotal ? `${activeDraft.startTime} 至 ${activeDraft.endTime}，共 ${activeTotal} 条` : '设置模板发布数量后生成'}</span></div>{activeTotal ? <div className="publish-preview-metrics"><span>平均间隔<b>{formatTimeGap(activeTimeMetrics.averageGap)}</b></span><span>最短间隔<b>{formatTimeGap(activeTimeMetrics.shortestGap)}</b></span></div> : null}</div>{activeTotal ? <><div className="publish-timeline"><span>{activeDraft.startTime}</span><div className="publish-timeline-track"><i/>{activeTimes.map((time, index) => { const template = templateMap.get(activeTasks[index]?.templateId); return <em className={`type-${template?.type || 'image'}`} style={{ left: `${getTimePosition(time, activeDraft.startTime, activeDraft.endTime)}%` }} title={`${time} · ${template?.name || '内容模板'}`} key={`${time}-${index}`}/>} )}</div><span>{activeDraft.endTime}</span></div><div className="publish-preview-times">{activeTimes.slice(0, 12).map((time, index) => { const template = templateMap.get(activeTasks[index]?.templateId); return <span className={`type-${template?.type || 'image'}`} title={template?.name} key={`${time}-${index}`}>{time}</span> })}{activeTimes.length > 12 ? <b>+{activeTimes.length - 12}</b> : null}</div></> : null}</div>
      </section>
    </div>
  </ModalShell>
}

export function QuickActionHost() {
  const { dialog, closeDialog } = useProjectData()
  if (!dialog) return null
  if (dialog.type === 'upload') return <UploadWorkDialog payload={dialog.payload} close={closeDialog}/>
  if (dialog.type === 'template') return <TemplateDialog payload={dialog.payload} close={closeDialog}/>
  if (dialog.type === 'publishSettings') return <PublishSettingsDialog payload={dialog.payload} close={closeDialog}/>
  return null
}
